import { ActivatedChallengePurchasesResponse, OrganizationPurchaseHistory, OrganizationSubscriptionResponse, PurchaseSummaryResponse } from '../../types';
import { get, post, throwHttpError } from './api';

const API_BASE_URL = '/api';

/**
 * Get purchase history for an organization
 */
export async function getOrganizationPurchaseHistory(
    organizationId: string,
    target_entity_id?: string,
    organizationKey?: string
): Promise<OrganizationPurchaseHistory> {
    const params = new URLSearchParams();
    if (organizationKey) {
        params.append('organization_key', organizationKey);
    }

    if (target_entity_id) {
        params.append('target_entity_id', target_entity_id);
    }

    const url = `${API_BASE_URL}/organizations/${organizationId}/purchase_history?${params.toString()}`;

    const response = await fetch(url, get());

    if (!response.ok) {
        // Return empty state if endpoint doesn't exist yet
        if (response.status === 404) {
            return {
                transactions: [],
                total_revenue_cents: []
            };
        }
        throw new Error(`Failed to get purchase history: ${response.statusText}`);
    }

    return await response.json();
}

export async function get_transaction_status(pending_transaction_id: string): Promise<PurchaseSummaryResponse> {
    const url = `${API_BASE_URL}/transaction/${pending_transaction_id}`;

    const response = await fetch(url, get());
    if (!response.ok) {
        await throwHttpError('An error occurred while starting the checkout session.', response);
    }

    return await response.json() as PurchaseSummaryResponse;
}

export async function getSubscriptionById(subscription_id: string, organization_id: string): Promise<OrganizationSubscriptionResponse> {
    const url = `${API_BASE_URL}/organizations/${organization_id}/subscriptions/${subscription_id}`;

    const response = await fetch(url, get());
    if (!response.ok) {
        await throwHttpError('An error occurred while getting the subscription details.', response);
    }

    return await response.json() as OrganizationSubscriptionResponse;
}

export async function cancel_subscription(organization_id: string, subscription_id: string): Promise<void> {
    const url = `${API_BASE_URL}/payment_account/subscription/cancel`;
    const response = await fetch(url, post({
        organization_id,
        subscription_id,
    }));

    if (!response.ok) {
        await throwHttpError('Unable to cancel subscription.', response);
    }
}

export async function get_purchases_for_entity(
    created_entity_id: string,
    target_entity_type?: string,
    entity_key?: string
): Promise<PurchaseSummaryResponse[]> {
    const params = new URLSearchParams();
    if (target_entity_type) {
        params.append('target_entity_type', target_entity_type);
    }
    if (entity_key) {
        params.append('entity_key', entity_key);
    }

    const url = `${API_BASE_URL}/transactions/by_entity/${created_entity_id}?${params.toString()}`;

    const response = await fetch(url, get());
    if (!response.ok) {
        await throwHttpError('An error occurred while getting purchases for the entity.', response);
    }

    return await response.json() as PurchaseSummaryResponse[];
}


export async function get_activated_challenge_purchases(
    activated_challenge_id: string,
    activated_challenge_key?: string
): Promise<ActivatedChallengePurchasesResponse> {
    const params = new URLSearchParams();
    if (activated_challenge_key) {
        params.append('activated_challenge_key', activated_challenge_key);
    }

    const url = `${API_BASE_URL}/activated_challenges/${activated_challenge_id}/purchases?${params.toString()}`;

    const response = await fetch(url, get());
    if (!response.ok) {
        await throwHttpError('An error occurred while getting purchases for the activated challenge.', response);
    }

    return await response.json() as ActivatedChallengePurchasesResponse;
}